// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * @param {boolean} showArrowsOutside - If true, displays navigation arrows outside the swiper container to fix overflow issues.
 * @param {object} xController - Custom controller instance to control swiper behavior externally.
 * @param {object} extendedStyles - Additional styles to be appended to the component.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import ArrowNavigation from "@synd/App/Components/XngCarousel/ArrowNavigation";
import { CarouselContainer, syndCarouseStyles } from "@synd/App/Components/XngCarousel/styles";
import { SwiperInstance } from "@synd/App/Components/XngCarousel/types";
import { mergeWithCss } from "@synd/XDL/Utilities";
import pick from "lodash/pick";
import React, { FC, isValidElement, useRef, useState } from "react";

import {
    A11y,
    Autoplay,
    Controller,
    EffectCoverflow,
    EffectFade,
    FreeMode,
    Navigation,
    Pagination,
    Scrollbar,
    Zoom,
} from "swiper/modules";
import { Swiper, SwiperProps, SwiperSlide } from "swiper/react";

interface Props extends SwiperProps {
    lazy?: boolean;
    shadowWidth?: string;
    withShadow?: boolean;
    shadowColor?: string;
    persistShadow?: boolean;
    slideMaxWidth?: string;
    slideWidth?: string;
    arrowsOnMobile?: boolean;
    showArrowsOutside?: boolean;
    hideArrowOnDisable?: boolean;
    extendedStyles?: XngCarouselStyles;
}

export interface XngCarouselStyles {
    container?: InjectableCss;
}

const XngCarousel: FC<Props> = ({
    children,
    className: classNameProps,
    extendedStyles,
    lazy = true,
    withShadow = true,
    shadowWidth = "0",
    shadowColor = "common.white",
    persistShadow = false,
    slideMaxWidth,
    slideWidth = "auto",
    slidesPerView = "auto",
    arrowsOnMobile = false,
    showArrowsOutside = false,
    hideArrowOnDisable = false,
    ...swiperProps
}) => {
    const [styles] = useState(() => mergeWithCss(syndCarouseStyles, extendedStyles));
    const [prevDisabled, setPrevDisabled] = useState(true);
    const [nextDisabled, setNextDisabled] = useState(false);
    const swiperInstanceRef = useRef<SwiperInstance | null>(null);

    let paginationOptions;
    let scrollbarOptions;

    if (swiperProps.pagination) {
        paginationOptions =
            typeof swiperProps.pagination === "boolean"
                ? { el: ".swiper-pagination", clickable: true }
                : swiperProps.pagination;
    }

    if (swiperProps.scrollbar) {
        scrollbarOptions =
            typeof swiperProps.scrollbar === "boolean"
                ? { draggable: true, el: ".swiper-scrollbar" }
                : swiperProps.scrollbar;
    }

    if (swiperProps.zoom) {
        if (typeof swiperProps.zoom === "boolean") {
            swiperProps.zoom = {
                toggle: false,
            };
        } else {
            swiperProps.zoom = {
                ...swiperProps.zoom,
                toggle: false,
            };
        }
    }

    const isVertical = swiperProps.direction === "vertical";

    return (
        <CarouselContainer
            {...styles.container}
            className={classNameProps}
            withShadow={withShadow}
            shadowWidth={shadowWidth}
            prevDisabled={prevDisabled}
            nextDisabled={nextDisabled}
            persistShadow={persistShadow}
            shadowColor={shadowColor}
            withNavigation={swiperProps.navigation}
            slideMaxWidth={slideMaxWidth}
            direction={swiperProps.direction}
            height={swiperProps.height}
            showArrowsOutside={showArrowsOutside}
            showOnMobile={arrowsOnMobile}
            isVertical={isVertical}
            slideWidth={slideWidth}
        >
            {swiperProps.navigation && showArrowsOutside && (
                <ArrowNavigation
                    onDisabled={disabled => setPrevDisabled(!!swiperProps.loop && !!disabled)}
                    arrowType="prev"
                    showOnMobile={arrowsOnMobile}
                    showNavigation={!!swiperProps.navigation}
                    swiperInstanceRef={swiperInstanceRef}
                    showArrowsOutside={showArrowsOutside}
                    hideArrowOnDisable={hideArrowOnDisable}
                />
            )}
            <Swiper
                {...swiperProps}
                onSwiper={swiper => {
                    swiperProps.onSwiper?.(swiper);
                    swiperInstanceRef.current = swiper;
                }}
                pagination={paginationOptions}
                scrollbar={scrollbarOptions}
                modules={[
                    Zoom,
                    Navigation,
                    Pagination,
                    Scrollbar,
                    A11y,
                    Controller,
                    Autoplay,
                    EffectFade,
                    FreeMode,
                    EffectCoverflow,
                ]}
                loop={!!swiperProps.loop}
                navigation
                // Add observer/observeParents in-order to re-init in case of content change
                observeParents
                observer
                {...(swiperProps.effect === "coverflow" && {
                    coverflowEffect: {
                        rotate: 0,
                        stretch: 0,
                        depth: 0,
                        modifier: 1,
                        slideShadows: false,
                    },
                })}
                slidesPerView={slidesPerView}
                onClick={(swiper, event) => {
                    if (swiperProps?.zoom) {
                        swiper.zoom.toggle(event);
                    }
                }}
            >
                {React.Children.map(children, child => {
                    if (!isValidElement(child)) {
                        return;
                    }

                    const { props } = child;
                    const swiperSlidePropsKeys = ["tag", "zoom", "virtualIndex"];
                    const childrenProps = omitMultiple(props as any, [...swiperSlidePropsKeys]);
                    const swiperSlideProps = pick(props, swiperSlidePropsKeys);

                    return (
                        <SwiperSlide {...swiperSlideProps}>
                            {React.createElement(child.type, childrenProps)}
                            {lazy && <div className="swiper-lazy-preloader"></div>}
                        </SwiperSlide>
                    );
                })}
                {swiperProps.navigation && !showArrowsOutside && !swiperProps.pagination && (
                    <>
                        <ArrowNavigation
                            onDisabled={disabled => setPrevDisabled(!!swiperProps.loop && !!disabled)}
                            arrowType="prev"
                            showOnMobile={arrowsOnMobile}
                            showNavigation={!!swiperProps.navigation}
                            showArrowsOutside={showArrowsOutside}
                            hideArrowOnDisable={hideArrowOnDisable}
                        />
                        <ArrowNavigation
                            onDisabled={disabled => setNextDisabled(!!disabled)}
                            arrowType="next"
                            showOnMobile={arrowsOnMobile}
                            showNavigation={!!swiperProps.navigation}
                            showArrowsOutside={showArrowsOutside}
                            hideArrowOnDisable={hideArrowOnDisable}
                        />
                    </>
                )}
                {(swiperProps.pagination || (swiperProps.scrollbar && !isVertical)) && (
                    <>
                        <StyledWrapper className="xng-pagination-container">
                            {swiperProps.navigation && !swiperProps.scrollbar && (
                                <ArrowNavigation
                                    arrowType="prev"
                                    showOnMobile={true}
                                    onDisabled={disabled => setPrevDisabled(!!disabled)}
                                />
                            )}
                            {swiperProps.pagination && <div className="swiper-pagination" />}
                            {swiperProps.scrollbar && !isVertical && <div className="swiper-scrollbar" />}
                            {swiperProps.navigation && !swiperProps.scrollbar && (
                                <ArrowNavigation
                                    arrowType="next"
                                    showOnMobile={true}
                                    onDisabled={disabled => setNextDisabled(!!disabled)}
                                />
                            )}
                        </StyledWrapper>
                    </>
                )}
                {swiperProps.scrollbar && isVertical && <div className="swiper-scrollbar" />}
            </Swiper>
            {swiperProps.navigation && showArrowsOutside && (
                <ArrowNavigation
                    onDisabled={disabled => setNextDisabled(!!disabled)}
                    arrowType="next"
                    showOnMobile={arrowsOnMobile}
                    showNavigation={!!swiperProps.navigation}
                    swiperInstanceRef={swiperInstanceRef}
                    showArrowsOutside={showArrowsOutside}
                    hideArrowOnDisable={hideArrowOnDisable}
                />
            )}
        </CarouselContainer>
    );
};

export default XngCarousel;
